import { canvasHeight, canvasWidth, initialZoom, Themes } from './data/constants';
import { StageDef, stages } from './data/maps';
import { ParticleManager } from './particleManager';
import { ColorTheme } from './types/ColorTheme';
import { MapEntityState } from './types/MapEntity.type';
import { SkillEffect } from './skillEffect';
import { GameObject } from './gameObject';
import { MarbleFrame, EntityFrame, decodeFrame } from './frameCodec';

/** Lightweight marble representation for viewer (no physics) */
interface ViewerMarble {
  id: number;
  name: string;
  hue: number;
  // current interpolated position
  x: number;
  y: number;
  angle: number;
  // target position (from latest frame)
  targetX: number;
  targetY: number;
  targetAngle: number;
  // skill cooltime ratio (0~1), -1 = disabled
  coolRatio: number;
}

export class ViewerRoulette {
  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private sizeFactor = 1;

  private marbles: ViewerMarble[] = [];
  private marbleMap: Map<number, ViewerMarble> = new Map();
  private winners: { name: string; hue: number }[] = [];
  private winnerName: string | null = null;
  private winnerRankIdx = -1;
  private nameHueMap: Map<string, number> = new Map();

  private stage: StageDef | null = null;
  private entities: MapEntityState[] = [];

  private cameraX = 12.95;
  private cameraY = 2;
  private cameraZoom = 1;
  private targetCameraX = 12.95;
  private targetCameraY = 2;
  private targetCameraZoom = 1;

  private theme: ColorTheme = Themes.dark;
  private particleManager = new ParticleManager();
  private effects: GameObject[] = [];

  private isRunning = false;
  private lastTime = 0;

  /** Initialize with a container element (no RoomClient dependency) */
  init(container: HTMLElement) {
    this.initCanvas(container);
    this.updateLoop();
  }

  private initCanvas(container: HTMLElement) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = canvasWidth;
    this.canvas.height = canvasHeight;
    this.canvas.id = 'viewer-canvas';
    this.ctx = this.canvas.getContext('2d', { alpha: false }) as CanvasRenderingContext2D;

    container.appendChild(this.canvas);

    const resizing = (entries?: ResizeObserverEntry[]) => {
      const realSize = entries
        ? entries[0].contentRect
        : this.canvas.getBoundingClientRect();
      const width = Math.max(realSize.width / 2, 640);
      const height = (width / realSize.width) * realSize.height;
      this.canvas.width = width;
      this.canvas.height = height;
      this.sizeFactor = width / realSize.width;
    };

    const resizeObserver = new ResizeObserver(resizing);
    resizeObserver.observe(this.canvas);
    resizing();
  }

  /** Called by the game plugin to set up the game (names + map) */
  setupGame(names: string[], mapId: number) {
    this.stage = stages[mapId] || stages[0];
    this.loadStageEntities();

    this.marbles = [];
    this.marbleMap.clear();
    this.winners = [];
    this.winnerName = null;
    this.winnerRankIdx = -1;
    this.effects = [];
    this.nameHueMap.clear();

    const totalCount = names.length;
    for (let i = 0; i < totalCount; i++) {
      const hue = (360 / totalCount) * i;
      const marble: ViewerMarble = {
        id: i,
        name: names[i],
        hue,
        x: 10.25 + (i % 10) * 0.6,
        y: Math.ceil(totalCount / 10) - Math.floor(i / 10),
        angle: 0,
        targetX: 10.25 + (i % 10) * 0.6,
        targetY: Math.ceil(totalCount / 10) - Math.floor(i / 10),
        targetAngle: 0,
        coolRatio: -1,
      };
      this.marbles.push(marble);
      this.marbleMap.set(i, marble);
      this.nameHueMap.set(names[i], hue);
    }

    this.isRunning = false;
  }

  /** Called by the game plugin when game starts */
  startGame() {
    this.isRunning = true;
  }

  /** Called by the game plugin to apply a binary frame */
  applyFrameData(data: ArrayBuffer) {
    const frame = decodeFrame(data);
    this.applyFrame(frame.marbles, frame.winners, frame.camera);
    this.applyEntityStates(frame.entities);
  }

  /** Called by the game plugin to show goal result */
  showGoal(winner: string, rankIdx: number) {
    this.winnerName = winner;
    this.winnerRankIdx = rankIdx;
    this.isRunning = false;
    this.particleManager.shot(this.canvas.width, this.canvas.height);
  }

  /** Called by the game plugin to add skill effect */
  addSkillEffect(x: number, y: number) {
    this.effects.push(new SkillEffect(x, y));
  }

  /** Called by the game plugin to reset (keep rank display until next setupGame) */
  resetGame() {
    this.isRunning = false;
    this.effects = [];
  }

  private loadStageEntities() {
    if (!this.stage || !this.stage.entities) {
      this.entities = [];
      return;
    }
    // Convert MapEntity[] to MapEntityState[] (static positions)
    this.entities = this.stage.entities.map((e) => ({
      x: e.position.x,
      y: e.position.y,
      angle: 0,
      shape: e.shape,
      life: e.props?.life ?? -1,
    }));
  }

  private applyFrame(
    frameMarbles: MarbleFrame[],
    frameWinners: string[],
    camera: { x: number; y: number; zoom: number },
  ) {
    // Update marble targets
    for (const fm of frameMarbles) {
      const marble = this.marbleMap.get(fm.id);
      if (marble) {
        marble.targetX = fm.x;
        marble.targetY = fm.y;
        marble.targetAngle = fm.a;
        marble.coolRatio = fm.c;
      }
    }

    // Remove marbles that are no longer in the frame (by ID)
    // Also filter by winner names as a safety net (goal event fires before _marbles.filter runs)
    const activeIds = new Set(frameMarbles.map(m => m.id));
    const winnerNameSet = new Set(frameWinners);
    this.marbles = this.marbles.filter(m => activeIds.has(m.id) && !winnerNameSet.has(m.name));

    this.winners = frameWinners.map(name => ({
      name,
      hue: this.nameHueMap.get(name) ?? 0,
    }));
    this.targetCameraX = camera.x;
    this.targetCameraY = camera.y;
    this.targetCameraZoom = camera.zoom;
  }

  private applyEntityStates(entityFrames: EntityFrame[]) {
    if (!entityFrames || entityFrames.length === 0) return;
    for (let i = 0; i < entityFrames.length && i < this.entities.length; i++) {
      this.entities[i].angle = entityFrames[i].angle;
      this.entities[i].life = entityFrames[i].alive ? -1 : 0;
    }
  }

  private updateLoop = () => {
    if (!this.lastTime) this.lastTime = Date.now();
    const now = Date.now();
    const dt = now - this.lastTime;
    this.lastTime = now;

    // Interpolate marble positions
    const lerpFactor = Math.min(1, dt / 33); // ~30fps target
    for (const marble of this.marbles) {
      marble.x += (marble.targetX - marble.x) * lerpFactor;
      marble.y += (marble.targetY - marble.y) * lerpFactor;
      marble.angle += (marble.targetAngle - marble.angle) * lerpFactor;
    }

    // Interpolate camera
    this.cameraX += (this.targetCameraX - this.cameraX) * lerpFactor;
    this.cameraY += (this.targetCameraY - this.cameraY) * lerpFactor;
    this.cameraZoom += (this.targetCameraZoom - this.cameraZoom) * lerpFactor;

    // Update effects
    this.effects.forEach(e => e.update(dt));
    this.effects = this.effects.filter(e => !e.isDestroy);

    // Update particles
    this.particleManager.update(dt);

    // Sort marbles by Y (same as original)
    this.marbles.sort((a, b) => b.y - a.y);

    this.render();
    requestAnimationFrame(this.updateLoop);
  };

  private render() {
    if (!this.stage) {
      // Draw waiting screen
      this.ctx.fillStyle = this.theme.background;
      this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
      this.ctx.fillStyle = '#999';
      this.ctx.font = '24px sans-serif';
      this.ctx.textAlign = 'center';
      this.ctx.fillText('호스트가 게임을 준비 중입니다...', this.canvas.width / 2, this.canvas.height / 2);
      this.ctx.font = '16px sans-serif';
      this.ctx.fillStyle = '#666';
      this.ctx.fillText('잠시만 기다려주세요', this.canvas.width / 2, this.canvas.height / 2 + 35);
      return;
    }

    // Clear
    this.ctx.fillStyle = this.theme.background;
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Apply initial zoom and camera
    this.ctx.save();
    this.ctx.scale(initialZoom, initialZoom);
    this.ctx.textAlign = 'left';
    this.ctx.textBaseline = 'top';
    this.ctx.font = '0.4pt sans-serif';
    this.ctx.lineWidth = 3 / (this.cameraZoom + initialZoom);

    // Camera transform
    this.ctx.save();
    const zoomFactor = initialZoom * 2 * this.cameraZoom;
    this.ctx.translate(-this.cameraX * this.cameraZoom, -this.cameraY * this.cameraZoom);
    this.ctx.scale(this.cameraZoom, this.cameraZoom);
    this.ctx.translate(
      this.canvas.width / zoomFactor,
      this.canvas.height / zoomFactor,
    );

    // Draw entities
    this.renderEntities();

    // Draw effects
    this.effects.forEach(e => e.render(this.ctx, this.cameraZoom * initialZoom, this.theme));

    // Draw marbles
    this.renderMarbles();

    this.ctx.restore();
    this.ctx.restore();

    // Draw rank list
    this.renderRankList();

    // Draw particles
    this.particleManager.render(this.ctx);

    // Draw winner overlay
    this.renderWinner();
  }

  private renderEntities() {
    this.ctx.save();
    for (const entity of this.entities) {
      if (entity.life === 0) continue;
      const transform = this.ctx.getTransform();
      this.ctx.translate(entity.x, entity.y);
      this.ctx.rotate(entity.angle);
      this.ctx.fillStyle = entity.shape.color ?? this.theme.entity[entity.shape.type].fill;
      this.ctx.strokeStyle = entity.shape.color ?? this.theme.entity[entity.shape.type].outline;
      this.ctx.shadowBlur = this.theme.entity[entity.shape.type].bloomRadius;
      this.ctx.shadowColor = entity.shape.bloomColor ?? entity.shape.color ?? this.theme.entity[entity.shape.type].bloom;

      const shape = entity.shape;
      switch (shape.type) {
        case 'polyline':
          if (shape.points.length > 0) {
            this.ctx.beginPath();
            this.ctx.moveTo(shape.points[0][0], shape.points[0][1]);
            for (let i = 1; i < shape.points.length; i++) {
              this.ctx.lineTo(shape.points[i][0], shape.points[i][1]);
            }
            this.ctx.stroke();
          }
          break;
        case 'box': {
          const w = shape.width * 2;
          const h = shape.height * 2;
          this.ctx.rotate(shape.rotation);
          this.ctx.fillRect(-w / 2, -h / 2, w, h);
          this.ctx.strokeRect(-w / 2, -h / 2, w, h);
          break;
        }
        case 'circle':
          this.ctx.beginPath();
          this.ctx.arc(0, 0, shape.radius, 0, Math.PI * 2, false);
          this.ctx.stroke();
          break;
      }
      this.ctx.setTransform(transform);
    }
    this.ctx.restore();
  }

  private renderMarbles() {
    const zoom = this.cameraZoom * initialZoom;
    const size = 0.5;
    const hs = size / 2;
    const startAngle = Math.PI * 1.5; // 270 degrees

    for (const marble of this.marbles) {
      // Draw marble circle
      this.ctx.fillStyle = `hsl(${marble.hue} 100% ${this.theme.marbleLightness}%)`;
      this.ctx.beginPath();
      this.ctx.arc(marble.x, marble.y, hs, 0, Math.PI * 2);
      this.ctx.fill();

      // Draw skill cooltime gauge
      if (marble.coolRatio >= 0) {
        this.ctx.strokeStyle = this.theme.coolTimeIndicator;
        this.ctx.lineWidth = 1 / zoom;
        this.ctx.beginPath();
        this.ctx.arc(
          marble.x, marble.y,
          hs + 2 / zoom,
          startAngle,
          startAngle + Math.PI * 2 * marble.coolRatio,
        );
        this.ctx.stroke();
      }

      // Draw name
      this.ctx.save();
      this.ctx.font = '12pt sans-serif';
      this.ctx.strokeStyle = 'black';
      this.ctx.lineWidth = 2;
      this.ctx.fillStyle = `hsl(${marble.hue} 100% ${this.theme.marbleLightness}%)`;
      this.ctx.translate(marble.x, marble.y + 0.25);
      this.ctx.scale(1 / zoom, 1 / zoom);
      this.ctx.strokeText(marble.name, 0, 0);
      this.ctx.fillText(marble.name, 0, 0);
      this.ctx.restore();
    }
  }

  private renderRankList() {
    const startX = this.canvas.width - 5;
    const fontHeight = 16;

    this.ctx.save();
    this.ctx.textAlign = 'right';
    this.ctx.font = '10pt sans-serif';
    this.ctx.fillStyle = '#666';
    this.ctx.fillText(
      `${this.winners.length} / ${this.winners.length + this.marbles.length}`,
      startX,
      fontHeight,
    );

    this.ctx.font = 'bold 11pt sans-serif';
    if (this.theme.rankStroke) {
      this.ctx.lineWidth = 2;
      this.ctx.strokeStyle = this.theme.rankStroke;
    }

    // Show last few winners at most
    const maxShow = Math.floor(this.canvas.height / fontHeight) - 2;
    const startIdx = Math.max(0, this.winners.length - maxShow);

    const listStartY = fontHeight * 2;  // 32px: count text is at y=16 (10pt font ~14px tall)
    for (let i = startIdx; i < this.winners.length; i++) {
      const y = (i - startIdx) * fontHeight;
      const winner = this.winners[i];
      const icon = i === this.winnerRankIdx ? '☆' : '\u2714';
      this.ctx.fillStyle = `hsl(${winner.hue} 100% ${this.theme.marbleLightness}%)`;
      const text = `${icon} ${winner.name} #${i + 1}`;
      if (this.theme.rankStroke) this.ctx.strokeText(text, startX, listStartY + y);
      this.ctx.fillText(text, startX, listStartY + y);
    }

    // Show remaining marbles
    this.ctx.font = '10pt sans-serif';
    for (let i = 0; i < Math.min(this.marbles.length, maxShow - this.winners.length + startIdx); i++) {
      const y = (this.winners.length - startIdx + i) * fontHeight;
      const marble = this.marbles[i];
      this.ctx.fillStyle = `hsl(${marble.hue} 100% ${this.theme.marbleLightness}%)`;
      const text = `${marble.name} #${i + 1 + this.winners.length}`;
      if (this.theme.rankStroke) this.ctx.strokeText(text, startX, listStartY + y);
      this.ctx.fillText(text, startX, listStartY + y);
    }

    this.ctx.restore();
  }

  private renderWinner() {
    if (!this.winnerName) return;

    const winnerMarble = this.marbles.find(m => m.name === this.winnerName)
      || this.winners.find(w => w.name === this.winnerName)
      || { hue: 0, name: this.winnerName };

    this.ctx.save();
    this.ctx.fillStyle = this.theme.winnerBackground;
    this.ctx.fillRect(
      this.canvas.width / 2,
      this.canvas.height - 168,
      this.canvas.width / 2,
      168,
    );

    // Marble circle
    const marbleSize = 100;
    const cx = this.canvas.width - marbleSize / 2 - 20;
    const cy = this.canvas.height - 168 / 2;
    this.ctx.beginPath();
    this.ctx.arc(cx, cy, marbleSize / 2, 0, Math.PI * 2);
    this.ctx.fillStyle = `hsl(${winnerMarble.hue} 100% ${this.theme.marbleLightness}%)`;
    this.ctx.fill();

    this.ctx.fillStyle = this.theme.winnerText;
    this.ctx.strokeStyle = this.theme.winnerOutline;
    this.ctx.font = 'bold 48px sans-serif';
    this.ctx.textAlign = 'right';
    this.ctx.lineWidth = 4;
    const textRightX = cx - marbleSize / 2 - 20;
    if (this.theme.winnerOutline) {
      this.ctx.strokeText('Winner', textRightX, this.canvas.height - 120);
    }
    this.ctx.fillText('Winner', textRightX, this.canvas.height - 120);

    this.ctx.font = 'bold 72px sans-serif';
    this.ctx.fillStyle = `hsl(${winnerMarble.hue} 100% ${this.theme.marbleLightness}%)`;
    if (this.theme.winnerOutline) {
      this.ctx.strokeText(winnerMarble.name, textRightX, this.canvas.height - 55);
    }
    this.ctx.fillText(winnerMarble.name, textRightX, this.canvas.height - 55);
    this.ctx.restore();
  }

  destroy() {
    this.canvas.remove();
  }
}
